// Utility for parsing {Token} merge fields in subject + body.

export const TOKEN_REGEX = /\{([^{}\n]+?)\}/g;

export function findTokens(...texts) {
    const seen = new Set();
    const ordered = [];
    for (const text of texts) {
        if (!text) continue;
        const matches = text.matchAll(TOKEN_REGEX);
        for (const m of matches) {
            const raw = m[1].trim();
            if (!raw) continue;
            const key = raw.toLowerCase();
            if (!seen.has(key)) {
                seen.add(key);
                ordered.push(raw);
            }
        }
    }
    return ordered;
}

// Substitute filled values into text/HTML. Preserves case in unfilled tokens.
export function substituteTokens(text, values) {
    if (!text) return "";
    return text.replace(TOKEN_REGEX, (full, name) => {
        const key = name.trim().toLowerCase();
        const found = Object.keys(values || {}).find((k) => k.toLowerCase() === key);
        if (found && values[found] && values[found].toString().trim() !== "") {
            return values[found];
        }
        return full;
    });
}

// For rendering HTML preview — replaces {tokens} with styled spans.
export function renderPreviewHtml(html, values) {
    if (!html) return "";
    return html.replace(TOKEN_REGEX, (full, name) => {
        const key = name.trim().toLowerCase();
        const found = Object.keys(values || {}).find((k) => k.toLowerCase() === key);
        const val = found ? values[found] : "";
        if (val && val.toString().trim() !== "") {
            const safe = String(val)
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;");
            return `<span class="token-filled">${safe}</span>`;
        }
        const safeName = name.trim()
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;");
        return `<span class="token-empty">{${safeName}}</span>`;
    });
}

export function renderPreviewSubject(subject, values) {
    return renderPreviewHtml(subject, values);
}

// Default style structural values — mirrors server-side STYLE_STRUCT_DEFAULTS.
// Matches the "Personal" seed so a bare preview looks like a plain email.
export const DEFAULT_STYLE = {
    name: "Personal",
    outer_bg: "#FFFFFF",
    inner_bg: "#FFFFFF",
    outer_border_enabled: false,
    outer_border_color: "#E2E6EA",
    outer_border_width: 1,
    inner_border_enabled: false,
    inner_border_color: "#E2E6EA",
    inner_border_width: 1,
    outer_padding: 0,
    inner_padding: 0,
    content_width: 624,
    content_align: "left",
};

function _mergeStyle(style) {
    return { ...DEFAULT_STYLE, ...(style || {}) };
}

// Alignment helpers — solve for Outlook/Word (reads align attributes) AND
// Gmail/others (read CSS). Returns everything needed by wrapEmailHtml.
function _alignForCopy(align) {
    switch (align) {
        case "center":
            return {
                outerTdAlign: "center",
                innerTableAlign: "center",
                innerMargin: "margin-left:auto;margin-right:auto;",
            };
        case "right":
            return {
                outerTdAlign: "right",
                innerTableAlign: "right",
                innerMargin: "margin-left:auto;margin-right:0;",
            };
        case "left":
        default:
            return {
                outerTdAlign: "left",
                innerTableAlign: "left",
                innerMargin: "margin-left:0;margin-right:auto;",
            };
    }
}

// Preview positioning inside the outer flex container.
function _alignForPreview(align) {
    if (align === "center") return "center";
    if (align === "right") return "flex-end";
    return "flex-start";
}

/** Build inline CSS/HTML for the outer wrapper table. Solid borders only,
 *  no border-radius — email-safe (Outlook/Word HTML engine).
 */
function _outerStyle(l) {
    const border = l.outer_border_enabled
        ? `border:${l.outer_border_width}px solid ${l.outer_border_color};`
        : "";
    return (
        `width:100%;background:${l.outer_bg};${border}` +
        `font-family:Poppins,Arial,sans-serif;`
    );
}

function _outerCellStyle(l) {
    return `padding:${l.outer_padding}px;`;
}

function _innerStyle(l, align) {
    const border = l.inner_border_enabled
        ? `border:${l.inner_border_width}px solid ${l.inner_border_color};`
        : "";
    return (
        `width:${l.content_width}px;max-width:100%;background:${l.inner_bg};${border}` +
        `color:#1A1A1A;${align.innerMargin}`
    );
}

function _innerCellStyle(l) {
    return `padding:${l.inner_padding}px;`;
}

// Wraps the body HTML in an email-safe outer + inner table structure driven
// by the resolved effective style. Options: { logo, style }.
export function wrapEmailHtml(bodyHtml, options) {
    let logo = null;
    let style = DEFAULT_STYLE;
    if (options && (options.absoluteUrl || options.logo || options.style || options.layout)) {
        if (options.absoluteUrl) {
            logo = options;
        } else {
            logo = options.logo || null;
            // Support legacy `layout` param name for a transition period.
            style = _mergeStyle(options.style || options.layout);
        }
    } else if (options) {
        logo = options;
    }

    const align = _alignForCopy(style.content_align);

    let logoRow = "";
    if (logo && logo.absoluteUrl) {
        const altSafe = String(logo.alt || "Brand logo")
            .replace(/&/g, "&amp;")
            .replace(/"/g, "&quot;")
            .replace(/</g, "&lt;");
        const h = Math.round(logo.height);
        logoRow =
            '<tr><td align="left" style="padding:0 0 20px;">' +
            `<img src="${logo.absoluteUrl}" alt="${altSafe}" height="${h}" ` +
            `style="height:${h}px;width:auto;max-width:340px;display:block;border:0;outline:none;text-decoration:none;" />` +
            '</td></tr>';
    }

    return (
        `<table role="presentation" cellspacing="0" cellpadding="0" border="0" ` +
        `bgcolor="${style.outer_bg}" width="100%" ` +
        `style="${_outerStyle(style)}">` +
        `<tr><td align="${align.outerTdAlign}" style="${_outerCellStyle(style)}">` +
        `<table role="presentation" align="${align.innerTableAlign}" cellspacing="0" cellpadding="0" border="0" ` +
        `bgcolor="${style.inner_bg}" width="${style.content_width}" ` +
        `style="${_innerStyle(style, align)}">` +
        `<tr><td style="${_innerCellStyle(style)}">` +
        logoRow +
        (logoRow ? `<div>${bodyHtml}</div>` : bodyHtml) +
        `</td></tr>` +
        `</table>` +
        `</td></tr></table>`
    );
}

/** Styles for the in-app preview — outer flex container aligns the inner
 *  panel left / centre / right so WYSIWYG matches the copied HTML.
 */
export function previewLayoutStyles(style) {
    const l = _mergeStyle(style);
    return {
        outer: {
            background: l.outer_bg,
            padding: `${l.outer_padding}px`,
            border: l.outer_border_enabled
                ? `${l.outer_border_width}px solid ${l.outer_border_color}`
                : "none",
            display: "flex",
            justifyContent: _alignForPreview(l.content_align),
        },
        inner: {
            width: l.content_width,
            maxWidth: "100%",
            background: l.inner_bg,
            border: l.inner_border_enabled
                ? `${l.inner_border_width}px solid ${l.inner_border_color}`
                : "none",
            padding: `${l.inner_padding}px`,
            color: "#1A1A1A",
            fontFamily: "Poppins, sans-serif",
            fontSize: 15,
            lineHeight: 1.6,
        },
    };
}

// Given a brand's active_logo object and the app backend URL, build the
// absolute-URL logo descriptor used by wrapEmailHtml.
export function buildEmailLogo(brand, backendUrl) {
    if (!brand || !brand.active_logo) return null;
    const logo = brand.active_logo;
    const height = Math.min(56, Math.max(20, Math.round(logo.display_height * 1.5)));
    return {
        absoluteUrl: `${backendUrl}${logo.url}`,
        alt: brand.label || brand.short_code || "Brand logo",
        height,
    };
}

// Plain-text fallback (strip tags)
export function htmlToPlainText(html) {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
}
